<div class="deposit-dialog">
  <div class="dialog-header">
    <div class="header-title">
      <mat-icon class="header-icon">account_balance_wallet</mat-icon>
      <div>
        <h2 mat-dialog-title class="title-text">Encaisser un acompte</h2>
        <span class="subtitle-text">Commande N° {{ data.orderId }}</span>
      </div>
    </div>
    <button mat-icon-button type="button" (click)="onCancel()" aria-label="Fermer" class="btn-close">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <mat-dialog-content class="dialog-content">
    <form [formGroup]="formGroup" (ngSubmit)="onSubmit()" id="depositForm">
      <!-- Out of pocket reminder -->
      <div class="remainder-card">
        <div class="remainder-info">
          <span class="remainder-label">Reste à charge patient actuel</span>
          <span class="remainder-value">{{ data.remainder | fc }}&nbsp;€</span>
        </div>
        <div class="legal-badge">
          <mat-icon class="badge-icon">gavel</mat-icon>
          <span>Conforme BOFIP / ISCA</span>
        </div>
      </div>

      <!-- Amount input & Quick percentage buttons -->
      <div class="form-section">
        <label class="section-label">Montant de l'acompte (TTC)</label>
        <div class="amount-input-row">
          <mat-form-field appearance="outline" class="amount-field">
            <mat-label>Montant TTC</mat-label>
            <input
              matInput
              type="number"
              step="0.01"
              [min]="minAmount"
              [max]="data.remainder"
              formControlName="amount"
              [errorStateMatcher]="amountErrorMatcher"
              (focus)="$any($event.target).select()"
              required />
            <span matTextSuffix>&nbsp;€</span>
            <!-- Une seule erreur à la fois : la zone réservée ne tient qu'une ligne -->
            @if (formGroup.get('amount')?.hasError('required')) {
              <mat-error>Le montant est requis</mat-error>
            } @else if (formGroup.get('amount')?.hasError('min') && minAmount < minDepositFloor) {
              <mat-error>Reste à charge sous {{ minDepositFloor | fc }} € : l'acompte doit le couvrir en entier, soit {{ minAmount | fc }} €</mat-error>
            } @else if (formGroup.get('amount')?.hasError('min')) {
              <mat-error>Acompte minimum : {{ minDepositPct }} % du reste à charge et {{ minDepositFloor | fc }} €, soit {{ minAmount | fc }} €</mat-error>
            } @else if (formGroup.get('amount')?.hasError('max')) {
              <mat-error>Ne peut pas dépasser le reste à charge ({{ data.remainder | fc }} €)</mat-error>
            }
          </mat-form-field>

          <mat-form-field appearance="outline" class="percent-field">
            <mat-label>Part</mat-label>
            <input
              matInput
              type="number"
              step="0.01"
              max="100"
              formControlName="percent"
              placeholder="0" />
            <span matTextSuffix>&nbsp;%</span>
          </mat-form-field>

          <div class="quick-presets">
            @for (pct of percentagePresets; track pct) {
              <button
                type="button"
                mat-stroked-button
                class="preset-btn"
                [attr.aria-pressed]="isPresetActive(pct)"
                (click)="setAmountPreset(pct)"
                [class.preset-btn-active]="isPresetActive(pct)">
                {{ pct * 100 }}%
              </button>
            }
          </div>
        </div>
      </div>

      <!-- Real-time VAT Breakdown Estimation -->
      @if (vatBreakdown.length > 0) {
        <div class="vat-preview-card">
          <div class="vat-header">
            <span class="vat-title">
              <mat-icon class="mini-icon">calculate</mat-icon>
              Ventilation TVA estimée (au prorata)
            </span>
            <span class="vat-note">Art. 269-2-c du CGI</span>
          </div>

          <table class="vat-table">
            <thead>
              <tr>
                <th class="col-rate">Taux TVA</th>
                <th class="col-ht text-right">Base HT</th>
                <th class="col-tva text-right">Montant TVA</th>
                <th class="col-ttc text-right">Total TTC</th>
              </tr>
            </thead>
            <tbody>
              @for (row of vatBreakdown; track row.vatRate) {
                <tr>
                  <td class="col-rate font-semibold">{{ row.vatRate | number:'1.1-2' }} %</td>
                  <td class="col-ht text-right">{{ row.baseHt | fc }} €</td>
                  <td class="col-tva text-right">{{ row.tva | fc }} €</td>
                  <td class="col-ttc text-right font-semibold">{{ row.ttc | fc }} €</td>
                </tr>
              }
              <tr class="vat-total-row font-semibold">
                <td class="col-rate">Total</td>
                <td class="col-ht text-right">{{ totalHt | fc }} €</td>
                <td class="col-tva text-right">{{ totalTva | fc }} €</td>
                <td class="col-ttc text-right text-primary">{{ formGroup.get('amount')?.value | fc }} €</td>
              </tr>
            </tbody>
          </table>
        </div>
      }

      <!-- Payment Method -->
      <div class="form-section">
        <label class="section-label">Règlement de l'acompte</label>
        <mat-form-field appearance="outline" class="w-100">
          <mat-label>Mode de paiement</mat-label>
          <mat-select formControlName="paymentMethod" required>
            @for (method of paymentMethods; track method.id) {
              <mat-option [value]="method.id" [disabled]="isCashLocked(method.id)">
                <span [class.cash-locked]="isCashLocked(method.id)" [matTooltip]="isCashLocked(method.id) ? cashNeedsDrawer : ''">{{ method.name }}</span>
              </mat-option>
            }
          </mat-select>
        </mat-form-field>

        <!-- Conditional fields: Chèque (id = 3) -->
        @if (selectedPaymentMethodId === 3) {
          <div class="conditional-fields-row">
            <mat-form-field appearance="outline" class="flex-1">
              <mat-label>N° de chèque</mat-label>
              <input matInput type="text" formControlName="checkNumber" placeholder="Ex: 1234567" />
            </mat-form-field>
            <mat-form-field appearance="outline" class="flex-1">
              <mat-label>Banque émettrice</mat-label>
              <input matInput type="text" formControlName="bankName" placeholder="Ex: BNP, Crédit Agricole..." />
            </mat-form-field>
          </div>
        }

        <!-- Conditional fields: CB (id = 1) -->
        @if (selectedPaymentMethodId === 1) {
          <div class="conditional-fields-row">
            <mat-form-field appearance="outline" class="w-100">
              <mat-label>Référence transaction / Autorisation CB (optionnel)</mat-label>
              <input matInput type="text" formControlName="cardRef" placeholder="Ex: AUTH-987456" />
            </mat-form-field>
          </div>
        }

        <!-- Conditional fields: Virement (id = 6) -->
        @if (selectedPaymentMethodId === 6) {
          <div class="conditional-fields-row">
            <mat-form-field appearance="outline" class="w-100">
              <mat-label>Référence virement bancaire</mat-label>
              <input matInput type="text" formControlName="transferRef" placeholder="Ex: VIR-ACOMPTE-CDE-{{ data.orderId }}" />
            </mat-form-field>
          </div>
        }

        <!-- Optional Note -->
        <mat-form-field appearance="outline" class="w-100">
          <mat-label>Note / Commentaire (optionnel)</mat-label>
          <textarea matInput rows="2" formControlName="note" placeholder="Commentaire affiché sur la facture d'acompte"></textarea>
        </mat-form-field>
      </div>
    </form>
  </mat-dialog-content>

  <mat-dialog-actions align="end" class="dialog-actions">
    <button mat-button type="button" (click)="onCancel()" [disabled]="isSubmitting">
      Annuler
    </button>
    <button
      mat-flat-button
      color="primary"
      type="submit"
      form="depositForm"
      [disabled]="formGroup.invalid || isSubmitting"
      class="btn-submit">
      @if (isSubmitting) {
        <mat-spinner diameter="18" class="btn-spinner"></mat-spinner>
        <span>Scellement en cours...</span>
      } @else {
        <ng-container>
          <mat-icon>lock</mat-icon>
          <span>Encaisser et sceller</span>
        </ng-container>
      }
    </button>
  </mat-dialog-actions>
</div>
